import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import Modal from '../../../components/Utils/ModalShow';

const formatAmount = (amount) => Number(amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 });

// Confirmation demandée par la clôture quand la comptabilisation laisserait
// des règles sans compte ou passerait une ligne d'ajustement (Yros/Support#57).
const CloseMonthWarningsModal = ({ warnings, onClose, onConfirm, isLoading }) => {
    const { t } = useTranslation();
    const unmappedRules = warnings?.unmapped_rules || [];
    const unflaggedRules = warnings?.unflagged_rules || [];
    const adjustmentTotal = warnings?.adjustment_total || 0;
    const externalIssues = warnings?.external_contract_issues || [];

    return (
        <Modal
            isOpen={!!warnings}
            onClose={onClose}
            title={t('closeMonthWarnings.title')}
            subtitle={t('closeMonthWarnings.subtitle')}
            headerIcon={<FaExclamationTriangle className="text-amber-600" size={20} />}
            size={640}
            footer={
                <>
                    <button
                        className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 font-medium"
                        onClick={onClose}
                    >
                        {t('basePrimeForm.cancel')}
                    </button>
                    <button
                        className="px-6 py-2 bg-[--color-primary] hover:bg-[--bg-hover] text-white rounded-lg transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        onClick={onConfirm}
                        disabled={isLoading}
                        data-cy="close-month-warnings-confirm"
                    >
                        <FaCheck size={14} />
                        {t('closeMonthWarnings.confirm')}
                    </button>
                </>
            }
        >
            <div className="p-6 space-y-4 text-sm text-gray-800" data-cy="close-month-warnings">
                {unmappedRules.length > 0 && (
                    <div>
                        <p className="font-medium">{t('closeMonthWarnings.unmappedIntro', { count: unmappedRules.length })}</p>
                        <ul className="mt-2 max-h-56 overflow-y-auto divide-y border rounded-lg">
                            {unmappedRules.map((rule) => (
                                <li key={rule.rule_id} className="flex justify-between gap-4 px-3 py-2">
                                    <span>{rule.name} <span className="text-gray-500">({rule.code})</span></span>
                                    <span className="font-medium whitespace-nowrap">{formatAmount(rule.amount)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {unflaggedRules.length > 0 && (
                    <div data-cy="close-month-warnings-unflagged">
                        <p className="font-medium">{t('closeMonthWarnings.unflaggedIntro', { count: unflaggedRules.length })}</p>
                        <ul className="mt-2 max-h-56 overflow-y-auto divide-y border rounded-lg">
                            {unflaggedRules.map((rule) => (
                                <li key={rule.rule_id} className="flex justify-between gap-4 px-3 py-2">
                                    <span>{rule.name} <span className="text-gray-500">({rule.code})</span></span>
                                    <span className="font-medium whitespace-nowrap">{formatAmount(rule.amount)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {adjustmentTotal > 0 && (
                    <p>{t('closeMonthWarnings.adjustment', { amount: formatAmount(adjustmentTotal) })}</p>
                )}
                {externalIssues.length > 0 && (
                    <div data-cy="close-month-warnings-external">
                        <p className="font-medium">{t('closeMonthWarnings.externalIntro', { count: externalIssues.length })}</p>
                        <ul className="mt-2 max-h-40 overflow-y-auto divide-y border rounded-lg">
                            {externalIssues.map((issue) => (
                                <li key={issue.contract_id} className="flex justify-between gap-4 px-3 py-2">
                                    <span>{issue.name}</span>
                                    <span className="text-gray-500 text-right">{issue.missing_accounts.join(', ')}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CloseMonthWarningsModal;
